/**
 * Response Pair Content Component
 * Renders the content section of a response pair with different tab views
 */

import React from 'react';
import { ChatMessage, ChatLocation } from '@/types/chatTypes';
import {
  PeopleAlsoAsk as PeopleAlsoAskType,
  ExperienceCard,
} from '@/constants/chatScreenData';
import { spacing } from '@/design-system/utils';
import { DiscoverTabContent } from './tabs/DiscoverTabContent';
import { PlacesTabContent } from './tabs/PlacesTabContent';
import { ExperiencesTabContent } from './tabs/ExperiencesTabContent';
import { MapTabContent } from './tabs/MapTabContent';
import { ImagesTabContent } from './tabs/ImagesTabContent';

export interface ResponsePairContentProps {
  assistantMessage: ChatMessage;
  activeTab: string;
  isLastPair: boolean;
  locations: ChatLocation[]; // Discover tab locations (real places only)
  allLocations: ChatLocation[]; // All valid locations including businesses
  onCardClick?: (cardId: string) => void;
  experiences: ExperienceCard[];
  onExperienceClick?: (experienceId: string) => void;
  overviewDescription: string;
  peopleAlsoAsk: PeopleAlsoAskType[];
  onPeopleAlsoAskClick?: (itemId: string, question: string) => void;
  onPlaceNameClick?: (placeName: string) => void;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onCopy?: (messageId?: string, content?: string) => void;
  onShare?: (messageId?: string, content?: string) => void;
  onDownload?: (messageId?: string, content?: string) => void;
  isLoading?: boolean;
  getFeedback: (messageId: string) => 'like' | 'dislike' | null;
}

export const ResponsePairContent: React.FC<ResponsePairContentProps> = ({
  assistantMessage,
  activeTab,
  isLastPair,
  locations,
  allLocations,
  onCardClick,
  experiences,
  onExperienceClick,
  overviewDescription,
  peopleAlsoAsk,
  onPeopleAlsoAskClick,
  onPlaceNameClick,
  onLike,
  onDislike,
  onCopy,
  onShare,
  onDownload,
  isLoading,
  getFeedback,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Discover Tab */}
      <DiscoverTabContent
        isActive={activeTab === 'discover'}
        assistantMessage={assistantMessage}
        overviewDescription={overviewDescription}
        experiences={experiences}
        onExperienceClick={onExperienceClick}
        locations={locations}
        onCardClick={onCardClick}
        onPlaceNameClick={onPlaceNameClick}
        peopleAlsoAsk={peopleAlsoAsk}
        onPeopleAlsoAskClick={onPeopleAlsoAskClick}
        onLike={onLike}
        onDislike={onDislike}
        onCopy={onCopy}
        onShare={onShare}
        onDownload={onDownload}
        getFeedback={getFeedback}
      />

      {/* Places Tab */}
      <PlacesTabContent
        isActive={activeTab === 'places'}
        locations={allLocations}
        onCardClick={onCardClick}
      />

      {/* Experiences Tab */}
      <ExperiencesTabContent
        isActive={activeTab === 'experiences'}
        isLastPair={isLastPair}
        isLoading={isLoading}
        experiences={experiences}
        onExperienceClick={onExperienceClick}
      />

      {/* Map Tab */}
      <MapTabContent
        isActive={activeTab === 'map'}
        experiences={experiences}
        locations={allLocations}
        onCardClick={onCardClick}
      />

      {/* Images Tab */}
      <ImagesTabContent isActive={activeTab === 'images'} />
    </div>
  );
};
